import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@/lib/api-client';
import { MutationConfig } from '@/lib/tanstack-query';
import { MembershipRole } from '@/types/api/membership';
import { Invite } from '@/types/api/invite';
import { getGroupQueryOptions } from '@/features/groups/api/get-group';

export const CreateInviteInputSchema = z.object({
  roles: z.array(z.nativeEnum(MembershipRole)),
});

export type CreateInviteInput = z.infer<typeof CreateInviteInputSchema>;

export type CreateInviteResult = Invite;

export const createMembershipInvitation = async ({
  data,
  orgId,
}: {
  data: CreateInviteInput;
  orgId: string;
}): Promise<CreateInviteResult> => {
  return (await api.post(`/orgs/${orgId}/invites`, data)).data;
};

type UseCreateInviteOptions = {
  mutationConfig?: MutationConfig<typeof createMembershipInvitation>;
};

export const useCreateInvite = ({ mutationConfig }: UseCreateInviteOptions) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    onSuccess: (data, vars, ...args) => {
      // The INVITE_MEMBERS onboarding task is completed asynchronously via the message broker,
      // so mark the group stale and let the next mount refetch it rather than refetching right away
      queryClient.invalidateQueries({ queryKey: getGroupQueryOptions(vars.orgId).queryKey, refetchType: 'none' });
      onSuccess?.(data, vars, ...args);
    },
    ...restConfig,
    mutationFn: createMembershipInvitation,
  });
};
